import { t } from '@glory/i18n';
import {
  type Cell,
  type ColumnDef,
  createColumnHelper,
  createSortedRowModel,
  metaHelper,
  type Row,
  type RowData,
  rowSortingFeature,
  sortFn_alphanumeric,
  sortFn_basic,
  sortFn_datetime,
  sortFn_text,
  type SortingState,
  tableFeatures,
  useTable,
} from '@tanstack/react-table';
import { ArrowDown, ArrowUp } from 'lucide-react';
import {
  type CSSProperties,
  type KeyboardEvent,
  type ReactNode,
  useCallback,
  useEffect,
  useId,
  useState,
} from 'react';

import { cn } from '../lib/cn.ts';
import { SmoothHeight } from '../lib/motion.tsx';
import { SkeletonRows } from './skeleton.tsx';

/** Возможности таблиц платформы: сортировка на клиенте (функции, которые выбирает `sortFn: 'auto'`). */
export const dataTableFeatures = tableFeatures({
  rowSortingFeature,
  sortedRowModel: createSortedRowModel(),
  sortFns: {
    alphanumeric: sortFn_alphanumeric,
    basic: sortFn_basic,
    datetime: sortFn_datetime,
    text: sortFn_text,
  },
  columnMeta: metaHelper<DataColumnMeta>(),
});

export type DataTableFeatures = typeof dataTableFeatures;

/** Описание столбцов таблицы для строк типа T: `const col = dataColumns<Row>(); col.accessor(…)`. */
export const dataColumns = <TData extends RowData>() => createColumnHelper<DataTableFeatures, TData>();
export type DataColumn<TData extends RowData> = ColumnDef<DataTableFeatures, TData>;

export interface CellEditorContext<TData> {
  readonly row: TData;
  /** Сохранить значение и выйти из редактирования; next — перейти к следующей ячейке. */
  commit(next?: 'next' | 'previous'): void;
  /** Выйти без сохранения. */
  cancel(): void;
}

export interface DataColumnMeta {
  /** Числа — вправо, моноширинные цифры. */
  align?: 'left' | 'right';
  /** Ширина столбца по CSS grid: '2fr', '120px', 'minmax(0,1fr)'. */
  width?: string;
  headerClassName?: string;
  cellClassName?: string;
  /** Не показывать на узком экране (телефон): второстепенные столбцы. */
  hideOnMobile?: boolean;
  /**
   * Не обрезать содержимое по краям ячейки. Нужно там, где строка начинается с аватара: `truncate`
   * срезал бы его тень-обводку слева и сверху. Текст внутри такой ячейки обрезается сам.
   */
  noClip?: boolean;
  /**
   * Редактор ячейки: поле в режиме «ячейка». Enter или клик открывают, Esc отменяет.
   * Тип строки указывается в параметре: `(context: CellEditorContext<Task>) => …`.
   */
  editor?: (context: CellEditorContext<never>) => ReactNode;
}

interface EditingCell {
  readonly rowId: string;
  readonly columnId: string;
}

export interface DataTableProps<TData extends RowData> {
  data: readonly TData[];
  columns: DataColumn<TData>[];
  getRowId: (row: TData) => string;
  /** Активная строка (открыта в панели) — подсвечивается акцентом. */
  activeRowId?: string | null;
  /** Открыть строку: клик или Enter. */
  onRowActivate?: (row: TData) => void;
  sorting?: SortingState;
  onSortingChange?: (sorting: SortingState) => void;
  loading?: boolean;
  /** Что показать, если строк нет. */
  empty?: ReactNode;
  /** Строка итогов под таблицей: значения по id столбца, выравниваются как столбцы. */
  footer?: Partial<Record<string, ReactNode>>;
  /** Строка-действие в конце: «+ Задача». */
  append?: ReactNode;
  className?: string;
  'aria-label'?: string;
}

/** Шаблон сетки: полный — от планшета, без hideOnMobile — на телефоне. */
function gridStyle<TData extends RowData>(columns: DataColumn<TData>[]): CSSProperties {
  const width = (c: DataColumn<TData>) => c.meta?.width ?? 'minmax(0,1fr)';
  return {
    '--dt-cols': columns
      .filter((c) => !c.meta?.hideOnMobile)
      .map(width)
      .join(' '),
    '--dt-cols-md': columns.map(width).join(' '),
  } as CSSProperties;
}

const GRID = 'grid grid-cols-(--dt-cols) items-center gap-2 px-3 md:grid-cols-(--dt-cols-md)';

const columnClass = (meta: DataColumnMeta | undefined) =>
  cn(meta?.align === 'right' && 'text-right', meta?.hideOnMobile && 'hidden md:block');

/**
 * Таблица на CSS grid: сортировка по заголовкам, активная строка с навигацией ↑↓,
 * открытие строки по Enter, редактирование ячеек на месте.
 */
export function DataTable<TData extends RowData>({
  data,
  columns,
  getRowId,
  activeRowId,
  onRowActivate,
  sorting: controlledSorting,
  onSortingChange,
  loading = false,
  empty,
  footer,
  append,
  className,
  'aria-label': ariaLabel,
}: DataTableProps<TData>) {
  const [localSorting, setLocalSorting] = useState<SortingState>([]);
  const sorting = controlledSorting ?? localSorting;
  const table = useTable({
    features: dataTableFeatures,
    columns,
    data: data,
    getRowId: (row: TData) => getRowId(row),
    state: { sorting },
    onSortingChange: (updater) => {
      const next = typeof updater === 'function' ? updater(sorting) : updater;
      if (onSortingChange) onSortingChange(next);
      else setLocalSorting(next);
    },
  });
  const [editing, setEditing] = useState<EditingCell | null>(null);
  const bodyId = useId();
  const rows = table.getRowModel().rows;
  const grid = gridStyle(columns);

  const focusRow = useCallback(
    (rowId: string) => {
      document
        .getElementById(bodyId)
        ?.querySelector<HTMLElement>(`[data-row-id="${CSS.escape(rowId)}"]`)
        ?.focus();
    },
    [bodyId],
  );

  useEffect(() => {
    if (activeRowId && !editing) focusRow(activeRowId);
  }, [activeRowId, editing, focusRow]);

  const editableCells = (row: Row<DataTableFeatures, TData>) =>
    row.getAllCells().filter((cell) => cell.column.columnDef.meta?.editor);

  const moveEditing = (
    row: Row<DataTableFeatures, TData>,
    columnId: string,
    direction: 'next' | 'previous',
  ) => {
    const cells = editableCells(row);
    const index = cells.findIndex((c) => c.column.id === columnId);
    const target = cells[direction === 'next' ? index + 1 : index - 1];
    setEditing(target ? { rowId: row.id, columnId: target.column.id } : null);
    if (!target) focusRow(row.id);
  };

  const onRowKeyDown = (
    event: KeyboardEvent<HTMLDivElement>,
    row: Row<DataTableFeatures, TData>,
    index: number,
  ) => {
    if (event.target !== event.currentTarget) return;
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      const next = rows[index + (event.key === 'ArrowDown' ? 1 : -1)];
      if (next) {
        focusRow(next.id);
        if (activeRowId) onRowActivate?.(next.original);
      }
    } else if (event.key === 'Enter') {
      event.preventDefault();
      const [firstEditable] = editableCells(row);
      if (firstEditable && !onRowActivate) setEditing({ rowId: row.id, columnId: firstEditable.column.id });
      else onRowActivate?.(row.original);
    }
  };

  const renderCell = (row: Row<DataTableFeatures, TData>, cell: Cell<DataTableFeatures, TData>) => {
    const meta = cell.column.columnDef.meta;
    const isEditing = editing?.rowId === row.id && editing.columnId === cell.column.id;
    if (isEditing && meta?.editor) {
      const editor = meta.editor as (context: CellEditorContext<TData>) => ReactNode;
      return editor({
        row: row.original,
        commit: (next) => {
          if (next) moveEditing(row, cell.column.id, next);
          else {
            setEditing(null);
            focusRow(row.id);
          }
        },
        cancel: () => {
          setEditing(null);
          focusRow(row.id);
        },
      });
    }
    return <table.FlexRender cell={cell} />;
  };

  return (
    <div
      role="table"
      aria-label={ariaLabel}
      className={cn('overflow-hidden rounded-lg border-thick border-border bg-surface', className)}
    >
      <div role="rowgroup">
        {table.getHeaderGroups().map((group) => (
          <div key={group.id} role="row" className={cn(GRID, 'py-2')} style={grid}>
            {group.headers.map((header) => {
              const meta = header.column.columnDef.meta;
              const sorted = header.column.getIsSorted();
              const canSort = header.column.getCanSort();
              return (
                <div
                  key={header.id}
                  role="columnheader"
                  aria-sort={sorted === 'asc' ? 'ascending' : sorted === 'desc' ? 'descending' : undefined}
                  className={cn(
                    'min-w-0 truncate text-[0.6875rem] font-bold tracking-[0.05em] text-muted uppercase',
                    columnClass(meta),
                    meta?.headerClassName,
                  )}
                >
                  {header.isPlaceholder ? null : canSort ? (
                    <button
                      type="button"
                      onClick={header.column.getToggleSortingHandler()}
                      className={cn(
                        'inline-flex items-center gap-1 uppercase hover:text-text',
                        meta?.align === 'right' && 'flex-row-reverse',
                      )}
                      title={
                        sorted === 'asc'
                          ? t('ui.sortAscending')
                          : sorted === 'desc'
                            ? t('ui.sortDescending')
                            : undefined
                      }
                    >
                      <table.FlexRender header={header} />
                      {sorted === 'asc' ? (
                        <ArrowUp className="size-3" />
                      ) : sorted === 'desc' ? (
                        <ArrowDown className="size-3" />
                      ) : null}
                    </button>
                  ) : (
                    <table.FlexRender header={header} />
                  )}
                </div>
              );
            })}
          </div>
        ))}
      </div>
      {/* Строки после скелетона проявляются: группа монтируется заново, когда загрузка закончилась.
          Высота таблицы плавно идёт к новой — всё, что ниже (подсказка «ещё N», кнопки), не прыгает. */}
      <SmoothHeight>
        <div
          role="rowgroup"
          id={bodyId}
          key={loading ? 'loading' : 'rows'}
          className={cn(!loading && 'animate-fade-in')}
        >
          {loading ? (
            <SkeletonRows rows={5} columns={Math.min(columns.length, 5)} table />
          ) : rows.length === 0 ? (
            (empty ?? null)
          ) : (
            rows.map((row, index) => {
              const active = row.id === activeRowId;
              return (
                <div
                  key={row.id}
                  role="row"
                  tabIndex={0}
                  data-row-id={row.id}
                  aria-selected={activeRowId === undefined ? undefined : active}
                  onKeyDown={(e) => {
                    onRowKeyDown(e, row, index);
                  }}
                  onClick={
                    onRowActivate
                      ? () => {
                          onRowActivate(row.original);
                        }
                      : undefined
                  }
                  className={cn(
                    GRID,
                    'border-t border-border-soft py-2 outline-none transition-[background-color,box-shadow] duration-150 focus-visible:bg-surface-2',
                    onRowActivate && 'cursor-pointer hover:bg-surface-2',
                    active &&
                      'bg-selected shadow-[inset_3px_0_0_var(--g-accent)] hover:bg-selected focus-visible:bg-selected',
                  )}
                  style={grid}
                >
                  {row.getAllCells().map((cell) => {
                    const meta = cell.column.columnDef.meta;
                    const editable = Boolean(meta?.editor);
                    return (
                      <div
                        key={cell.id}
                        role="cell"
                        onClick={
                          editable
                            ? (e) => {
                                e.stopPropagation();
                                setEditing({ rowId: row.id, columnId: cell.column.id });
                              }
                            : undefined
                        }
                        className={cn(
                          'min-w-0',
                          meta?.noClip ? 'overflow-visible' : 'truncate',
                          columnClass(meta),
                          editable && 'cursor-text rounded-xs hover:bg-surface-2',
                          meta?.cellClassName,
                        )}
                      >
                        {renderCell(row, cell)}
                      </div>
                    );
                  })}
                </div>
              );
            })
          )}
        </div>
      </SmoothHeight>
      {footer ? (
        <div
          role="row"
          className={cn(GRID, 'border-t-[1.5px] border-strong py-2 font-extrabold')}
          style={grid}
        >
          {table.getAllLeafColumns().map((column) => {
            const meta = column.columnDef.meta;
            return (
              <div
                key={column.id}
                role="cell"
                className={cn('min-w-0 truncate', columnClass(meta), meta?.cellClassName)}
              >
                {footer[column.id]}
              </div>
            );
          })}
        </div>
      ) : null}
      {append ? (
        <div className="border-t border-dashed border-border px-3 py-2 font-bold text-muted">{append}</div>
      ) : null}
    </div>
  );
}
